/**
 * ALDER SEARCH — Senior Leadership Practice Page (Alder Talent)
 * Route: /senior-leadership
 * Brief: File 03 of 13 (v3.0)
 * Design: "Alder Forest — Organic Precision" — matches existing site exactly
 */

import { useEffect, useRef } from "react";
import { Link } from "wouter";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { media } from "@/data/media";

const services = [
  {
    number: "01",
    title: "Head of Product Search",
    description:
      "The Head of Product who can translate a VC or PE value creation thesis into a product roadmap — and ship against it — is not the same person as a Head of Product from a corporate background. We know the difference. We find the right one.",
    tags: ["VC- & PE-Backed Product", "Roadmap to Exit", "Product-Led Growth"],
    link: "/senior-leadership/head-of-product-search",
  },
  {
    number: "02",
    title: "VP Product Search",
    description:
      "VP Product leaders translate the value creation plan into product direction, customer outcomes, and disciplined execution. We place leaders who can build, scale, and improve the product organisation at VC- and PE-backed companies.",
    tags: ["VP Product", "Product Leadership", "Product Execution"],
    link: "/senior-leadership/vp-specialist-search",
  },
  {
    number: "03",
    title: "Senior IC & Staff Product Search",
    description:
      "Staff and Principal Product Managers are not roles you fill from a job board. The candidates are specific, the market is shallow, and the wrong hire is expensive. We place the practitioners who turn strategy into product work that ships.",
    tags: ["Senior IC", "Staff Product", "Principal Product"],
    link: "/senior-leadership/vp-specialist-search",
  },
  {
    number: "04",
    title: "Product Management Search",
    description:
      "Lead and Principal Product Managers turn product strategy into customer outcomes, roadmap progress, and disciplined delivery. We find the product leaders who think in commercial outcomes and execution.",
    tags: ["Lead Product Manager", "Principal Product Manager", "Product Delivery"],
    link: "/senior-leadership/vp-specialist-search",
  },
];

const roleTags = [
  "Head of Product",
  "VP Product",
  "Staff / Principal Product Manager",
  "Lead Product Manager",
];

export default function SeniorLeadershipPage() {
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);
  const sectionsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    document.title = "Product Leadership Search for VC- and PE-Backed Companies | Alder Talent";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Alder Talent places Head of Product, VP Product, and senior product individual contributors in VC- and PE-backed businesses. Retained. Niche. VC- and PE-exclusive.");
    }
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDesc = document.querySelector('meta[property="og:description"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogTitle) ogTitle.setAttribute("content", "Product Leadership Search for VC- and PE-Backed Companies | Alder Talent");
    if (ogDesc) ogDesc.setAttribute("content", "Alder Talent places Head of Product, VP Product, and senior product individual contributors in VC- and PE-backed businesses. Retained. Niche. VC- and PE-exclusive.");
    if (ogUrl) ogUrl.setAttribute("content", "https://aldersearch.com/senior-leadership");
    return () => { document.title = "Alder Search | Retained Product Executive Search for VC and PE"; };
  }, []);

  useEffect(() => {
    const allEls = [...cardsRef.current, ...sectionsRef.current];
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      allEls.forEach((el) => {
        if (!el) return;
        el.style.opacity = "1";
        el.style.transform = "none";
      });
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            el.style.opacity = "1";
            el.style.transform = "translateY(0)";
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
    );
    allEls.forEach((el, i) => {
      if (!el) return;
      el.style.opacity = "0";
      el.style.transform = "translateY(28px)";
      el.style.transition = `opacity 0.7s ease-out ${i * 70}ms, transform 0.7s ease-out ${i * 70}ms`;
      observer.observe(el);
    });

    // Fallback: ensure all elements become visible after 2s regardless
    const fallback = setTimeout(() => {
      allEls.forEach((el) => {
        if (!el) return;
        el.style.opacity = "1";
        el.style.transform = "translateY(0)";
      });
    }, 2000);

    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "oklch(0.978 0.006 80)" }}>
      <Navigation />

      {/* HERO */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden" style={{ background: "oklch(0.245 0.065 155)" }}>
        <picture className="absolute inset-0 block opacity-[0.18]">
          <source type="image/avif" srcSet={media.hero.avif} />
          <img src={media.hero.webp} srcSet={media.hero.srcSet} sizes="100vw" alt="" aria-hidden="true" width={media.hero.width} height={media.hero.height} className="h-full w-full object-cover" style={{ objectPosition: "center 30%" }} />
        </picture>
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, oklch(0.245 0.065 155 / 0.7) 0%, oklch(0.245 0.065 155 / 0.95) 100%)" }} />
        <div className="container relative z-10 pt-32 pb-24">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-10" style={{ background: "oklch(0.62 0.085 75)" }} />
              <span className="font-ui text-xs tracking-widest uppercase" style={{ fontFamily: "'DM Sans', system-ui, sans-serif", color: "oklch(0.62 0.085 75)", letterSpacing: "0.18em" }}>
                Alder Talent
              </span>
            </div>
            <h1 className="font-display mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "clamp(2.4rem, 5vw, 4rem)", color: "oklch(0.952 0.012 80)", lineHeight: 1.1, letterSpacing: "-0.02em" }}>
              The Leaders Who Build
              <br />
              <em style={{ fontStyle: "italic", color: "oklch(0.72 0.09 75)" }}>What the C-Suite Promises.</em>
            </h1>
            <p className="font-body mb-10 max-w-2xl" style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 400, fontSize: "1.1rem", lineHeight: 1.75, color: "oklch(0.75 0.02 155)" }}>
              Alder Talent is the senior leadership practice of Alder Search. We place the specialist product operators beneath the C-suite and the senior individual contributors beside it — Head of Product, VP Product, and senior IC practitioners — who determine whether the value creation plan actually gets executed.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link href="/#contact" className="font-ui text-sm px-8 py-3.5 transition-all duration-300 inline-block" style={{ fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: "0.75rem", background: "oklch(0.62 0.085 75)", color: "oklch(0.245 0.065 155)", borderRadius: "2px", textDecoration: "none" }}>
                Engage Alder Talent
              </Link>
              <Link href="/approach" className="font-ui text-sm px-8 py-3.5 transition-all duration-300 inline-block" style={{ fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 500, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: "0.75rem", border: "1px solid oklch(0.62 0.085 75 / 0.5)", color: "oklch(0.85 0.015 80)", borderRadius: "2px", textDecoration: "none" }}>
                Our Approach
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHY IT MATTERS */}
      <section className="py-28" style={{ background: "oklch(0.978 0.006 80)" }}>
        <div className="container">
          <div ref={(el) => { sectionsRef.current[0] = el; }} className="max-w-3xl">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-10" style={{ background: "oklch(0.62 0.085 75)" }} />
              <span className="font-ui text-xs tracking-widest uppercase" style={{ fontFamily: "'DM Sans', system-ui, sans-serif", color: "oklch(0.62 0.085 75)", letterSpacing: "0.18em" }}>
                Why It Matters
              </span>
            </div>
            <h2 className="font-display mb-8" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "clamp(2rem, 4vw, 3.2rem)", color: "oklch(0.245 0.065 155)", lineHeight: 1.15, letterSpacing: "-0.015em" }}>
              The Roles Your Returns Depend On.
            </h2>
            <div className="space-y-6">
              {[
                "Most VC and PE investors focus their talent attention on the C-suite. That's right. But the C-suite doesn't execute alone.",
                "The Head of Product owns the roadmap that drives product-led growth. Senior individual contributors turn strategy into work that ships. Get these roles wrong and you are asking your product leaders to build with the wrong materials.",
                "The talent market for these roles is deep in volume but thin in quality at the level VC and PE demand. The operators and senior practitioners who perform in a high-pressure, investor-backed environment are a specific subset. Finding them takes the same network and the same judgement as C-suite search. That's what Alder Talent is built to do.",
              ].map((para, i) => (
                <p key={i} className="font-body" style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 400, fontSize: "1.05rem", lineHeight: 1.8, color: "oklch(0.45 0.015 155)" }}>
                  {para}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES SECTION */}
      <section className="py-28" style={{ background: "oklch(0.96 0.008 80)" }}>
        <div className="container">
          <div ref={(el) => { sectionsRef.current[1] = el; }} className="mb-16">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-10" style={{ background: "oklch(0.62 0.085 75)" }} />
              <span className="font-ui text-xs tracking-widest uppercase" style={{ fontFamily: "'DM Sans', system-ui, sans-serif", color: "oklch(0.62 0.085 75)", letterSpacing: "0.18em" }}>
                What We Do
              </span>
            </div>
            <h2 className="font-display mb-4 max-w-2xl" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "clamp(2rem, 4vw, 3.2rem)", color: "oklch(0.245 0.065 155)", lineHeight: 1.15, letterSpacing: "-0.015em" }}>
              Senior Leadership &amp; Senior IC Search Built for VC &amp; PE.
            </h2>
            <p className="font-body max-w-2xl" style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 400, fontSize: "1.05rem", lineHeight: 1.75, color: "oklch(0.45 0.015 155)" }}>
              Every search is retained and exclusive. The same rigour, the same network, and the same product-specific assessment for VC and PE that Alder Search applies to leadership mandates — applied to the roles that sit beneath and beside them.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, i) => (
              <div
                key={service.number}
                ref={(el) => { cardsRef.current[i] = el; }}
                className="group relative p-8 flex flex-col"
                style={{ background: "white", border: "1px solid oklch(0.88 0.018 80)", borderRadius: "4px" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = "oklch(0.62 0.085 75)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = "oklch(0.88 0.018 80)"; }}
              >
                <div className="font-mono-accent mb-5" style={{ fontFamily: "'DM Mono', monospace", fontWeight: 300, fontSize: "0.75rem", color: "oklch(0.62 0.085 75)", letterSpacing: "0.1em" }}>
                  {service.number}
                </div>
                <h3 className="font-display mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 600, fontSize: "1.2rem", color: "oklch(0.245 0.065 155)", lineHeight: 1.3, letterSpacing: "-0.01em" }}>
                  {service.title}
                </h3>
                <p className="font-body mb-6 flex-1" style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 400, fontSize: "0.9rem", lineHeight: 1.75, color: "oklch(0.45 0.015 155)" }}>
                  {service.description}
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {service.tags.map((tag) => (
                    <span key={tag} className="font-ui text-xs px-2.5 py-1" style={{ fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 500, fontSize: "0.68rem", letterSpacing: "0.06em", textTransform: "uppercase", background: "oklch(0.952 0.012 80)", color: "oklch(0.32 0.07 155)", borderRadius: "2px" }}>
                      {tag}
                    </span>
                  ))}
                </div>
                <Link
                  href={service.link}
                  className="font-ui text-sm inline-flex items-center gap-1.5 transition-colors duration-200"
                  style={{ fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 600, fontSize: "0.78rem", letterSpacing: "0.04em", color: "oklch(0.245 0.065 155)", textDecoration: "none" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.62 0.085 75)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.245 0.065 155)"; }}
                >
                  Learn more →
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PULL QUOTE */}
      <section className="py-24 relative overflow-hidden" style={{ background: "oklch(0.245 0.065 155)" }}>
        <div className="absolute inset-0 opacity-5 pointer-events-none" style={{ backgroundImage: `radial-gradient(circle, oklch(0.62 0.085 75) 1px, transparent 1px)`, backgroundSize: "32px 32px" }} />
        <div className="container relative z-10">
          <div ref={(el) => { sectionsRef.current[2] = el; }} className="max-w-3xl mx-auto text-center">
            <div className="font-display mb-6 select-none" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "5rem", lineHeight: 0.6, color: "oklch(0.62 0.085 75)", opacity: 0.6 }}>"</div>
            <blockquote className="font-display mb-8" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 400, fontStyle: "italic", fontSize: "clamp(1.2rem, 2.5vw, 1.65rem)", lineHeight: 1.55, color: "oklch(0.952 0.012 80)", letterSpacing: "-0.01em" }}>
              Where Alder Search fills the boardroom, Alder Talent fills the roles that report to it. The Head of Product who builds the roadmap. The product practitioners who make it happen. Same standard. Same network. Same firm.
            </blockquote>
            <div className="flex items-center justify-center gap-3">
              <div className="h-px w-8" style={{ background: "oklch(0.62 0.085 75)" }} />
              <cite className="font-ui not-italic text-sm" style={{ fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 500, fontSize: "0.82rem", color: "oklch(0.62 0.085 75)", letterSpacing: "0.06em" }}>
                Alder Talent, Senior Leadership Practice
              </cite>
              <div className="h-px w-8" style={{ background: "oklch(0.62 0.085 75)" }} />
            </div>
          </div>
        </div>
      </section>

      {/* ROLES WE PLACE */}
      <section className="py-24" style={{ background: "oklch(0.978 0.006 80)" }}>
        <div className="container">
          <div ref={(el) => { sectionsRef.current[3] = el; }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-10" style={{ background: "oklch(0.62 0.085 75)" }} />
              <span className="font-ui text-xs tracking-widest uppercase" style={{ fontFamily: "'DM Sans', system-ui, sans-serif", color: "oklch(0.62 0.085 75)", letterSpacing: "0.18em" }}>
                Roles We Place
              </span>
            </div>
            <h2 className="font-display mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)", color: "oklch(0.245 0.065 155)", lineHeight: 1.15, letterSpacing: "-0.015em" }}>
              Niche. High-Calibre. Hard to Find.
            </h2>
            <p className="font-body mb-10" style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 400, fontSize: "1rem", lineHeight: 1.75, color: "oklch(0.45 0.015 155)" }}>
              These aren't roles you fill from a job board. They're roles you fill from a network.
            </p>
            <div className="flex flex-wrap gap-3">
              {roleTags.map((role) => (
                <span key={role} className="font-ui px-4 py-2" style={{ fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 500, fontSize: "0.82rem", letterSpacing: "0.03em", background: "white", color: "oklch(0.32 0.07 155)", border: "1px solid oklch(0.88 0.018 80)", borderRadius: "2px" }}>
                  {role}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW WE WORK (condensed) */}
      <section className="py-24" style={{ background: "oklch(0.96 0.008 80)" }}>
        <div className="container">
          <div ref={(el) => { sectionsRef.current[4] = el; }} className="max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-10" style={{ background: "oklch(0.62 0.085 75)" }} />
              <span className="font-ui text-xs tracking-widest uppercase" style={{ fontFamily: "'DM Sans', system-ui, sans-serif", color: "oklch(0.62 0.085 75)", letterSpacing: "0.18em" }}>
                How We Work
              </span>
            </div>
            <h2 className="font-display mb-5" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)", color: "oklch(0.245 0.065 155)", lineHeight: 1.15, letterSpacing: "-0.015em" }}>
              The Same Process. The Same Standard.
            </h2>
            <p className="font-body mb-8" style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 400, fontSize: "1.05rem", lineHeight: 1.8, color: "oklch(0.45 0.015 155)" }}>
              Alder Talent mandates run through the same four-phase methodology as every Alder Search engagement. Mandate alignment. Market mapping. Rigorous assessment. Placement and onboarding. The network is the same. The product-specific assessment for VC and PE is the same. The retained, exclusive model is the same. What changes is the level. What doesn't change is the quality.
            </p>
            <Link
              href="/approach"
              className="font-ui text-sm inline-flex items-center gap-2 transition-colors duration-200"
              style={{ fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 600, fontSize: "0.8rem", letterSpacing: "0.05em", color: "oklch(0.245 0.065 155)", textDecoration: "none" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.62 0.085 75)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.245 0.065 155)"; }}
            >
              Read our full approach →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-28 relative overflow-hidden" style={{ background: "oklch(0.18 0.055 155)" }}>
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: `radial-gradient(circle, oklch(0.62 0.085 75) 1px, transparent 1px)`, backgroundSize: "32px 32px" }} />
        <div className="container relative z-10">
          <div ref={(el) => { sectionsRef.current[5] = el; }} className="max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-10" style={{ background: "oklch(0.62 0.085 75)" }} />
              <span className="font-ui text-xs tracking-widest uppercase" style={{ fontFamily: "'DM Sans', system-ui, sans-serif", color: "oklch(0.62 0.085 75)", letterSpacing: "0.18em" }}>
                Engage Alder Talent
              </span>
            </div>
            <h2 className="font-display mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "clamp(2rem, 4vw, 3.2rem)", color: "oklch(0.952 0.012 80)", lineHeight: 1.15, letterSpacing: "-0.015em" }}>
              Start a Confidential Conversation.
            </h2>
            <p className="font-body mb-10" style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 300, fontSize: "1.05rem", lineHeight: 1.8, color: "oklch(0.75 0.02 155)" }}>
              Whether you have an active mandate at senior leadership level, are building out a portfolio company's product function, or want to understand how Alder Talent fits your investment thesis — we welcome the conversation.
            </p>
            <div className="flex flex-wrap gap-4 items-center">
              <Link href="/#contact" className="font-ui text-sm px-8 py-3.5 transition-all duration-300 inline-block" style={{ fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: "0.75rem", background: "oklch(0.62 0.085 75)", color: "oklch(0.245 0.065 155)", borderRadius: "2px", textDecoration: "none" }}>
                Engage Alder Talent
              </Link>
              <span className="font-ui text-xs" style={{ fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 400, fontSize: "0.78rem", color: "oklch(0.62 0.085 75)", letterSpacing: "0.04em", opacity: 0.8 }}>
                All enquiries are treated with strict confidentiality.
              </span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
